<template>
  <modal
    v-if="visible"
    :visible="visible"
    @click-outside-modal="$emit('close')"
  >
    <div
      class="h-full max-h-[calc(100vh-100px)] w-full max-w-[calc(100vw-30px)] overflow-auto rounded-md"
    >
      <div class="w-128 h-full max-w-lg rounded-md bg-white px-8 py-0">
        <div class="w-full">
          <div
            class="sticky top-0 z-40 flex justify-between bg-white pb-5 pt-8"
          >
            <div
              v-if="!showCreateAlertSuccess"
              class="flex items-center justify-between gap-2"
            >
              <h1 class="text-3xl font-medium">Identity Alert</h1>
              <svg
                class="h-8 w-8"
                viewBox="0 0 32 33"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.9681 9.72039C15.5378 8.73355 16.9622 8.73355 17.5319 9.72039L24.7108 22.1546C25.2806 23.1415 24.5684 24.375 23.4289 24.375H9.0711C7.93159 24.375 7.21941 23.1414 7.78916 22.1546L14.9681 9.72039Z"
                  stroke="#FF9330"
                  stroke-width="2"
                />
                <path
                  d="M16.25 13.5V19"
                  stroke="#FF9330"
                  stroke-width="2"
                />
                <circle
                  cx="16.25"
                  cy="21.25"
                  r="1.25"
                  fill="#FF9330"
                />
              </svg>
            </div>
            <div
              v-else
              class="flex items-center justify-between gap-2"
            >
              <h1 class="text-3xl font-medium">Alert Submitted</h1>
            </div>
          </div>
          <div
            v-if="!showCreateAlertSuccess"
            class="w-full"
            style="height: calc(100% - 88px)"
          >
            <div
              v-if="step === 1"
              class="h-full w-full"
            >
              <div class="mb-5">
                <p class="text-left">
                  What happens when an Identity Alert is submitted?
                </p>
              </div>

              <div class="flex w-full flex-col gap-y-4">
                <information-panel
                  v-for="(info, index) in informationPanels"
                  :key="`infoPanel-${index}`"
                  :info="info"
                />
              </div>
              <div class="mt-4 w-full">
                <checkbox
                  :item="checkboxText"
                  :handle-select="createAlertConfirmation"
                />
              </div>
              <div class="mt-4 flex h-20 w-full items-center pb-6">
                <button-main
                  variant="secondary"
                  class="mr-2 h-full w-full"
                  @click="onCancel"
                >
                  Cancel
                </button-main>
                <button-main
                  :disabled="!allowContinue"
                  class="mr-2 h-full w-full"
                  @click="stepForward()"
                >
                  Continue
                </button-main>
              </div>
            </div>

            <div
              v-else-if="step === 2"
              class="h-full w-full text-left"
            >
              <div class="mb-5">
                <p class="text-lg">Select Alert Type</p>
              </div>
              <div class="mt-5 text-body-color">
                We will determine whether an alert meets our publishing
                standards. Be prepared to provide hard evidence of the incident
                if necessary.
              </div>

              <div class="mt-4 flex w-full flex-col gap-y-4">
                <dropdown
                  field="alert_categories"
                  :options="alertCategories"
                  name="types"
                  placeholder="Select Type"
                  @dropdown-input="updateIsType"
                />
              </div>
              <div class="mt-6 flex h-20 w-full items-center pb-6">
                <button-main
                  variant="secondary"
                  class="mr-2 h-full w-full"
                  @click="stepBack()"
                >
                  Back
                </button-main>
                <button-main
                  :disabled="!allowContinue2"
                  class="h-full w-full"
                  @click="stepForward()"
                >
                  Continue
                </button-main>
              </div>
            </div>

            <div
              v-else-if="step === 3"
              class="h-full w-full text-left"
            >
              <div class="mb-5">
                <p class="text-lg">Alert Details</p>
              </div>

              <div class="mb-6">
                <p class="text-body-color">
                  We will determine whether an alert meets our publishing
                  standards. Be prepared to provide hard evidence of the
                  incident if necessary.
                </p>
              </div>
              <div class="flex w-full flex-col gap-y-4">
                <div class="relative w-full">
                  <form-field-date
                    label="Incident Date"
                    placeholder=""
                    :model-value="selectedAlertDate"
                    name="alert_date"
                    input-type="date"
                    :hidden="false"
                    :monospace="false"
                    :show-confirm-button="false"
                    @update:model-value="dynamicUpdateDate"
                    @toggle-date-picker="toggleShowDatePicker"
                  />
                  <div
                    v-show="showDatePicker"
                    class="absolute right-0 top-full z-50 rounded-lg bg-white p-4 shadow-md"
                  >
                    <date-picker
                      :initial-date="initialDate"
                      :field="'datepicker_identity_alert'"
                      date-format="MM/DD/YYYY"
                      :handle-input="setSelectedDay"
                      @day-selected="setSelectedDay"
                    />
                  </div>
                </div>
                <div
                  v-if="
                    payload.is_type === 'double_brokering' ||
                    payload.is_type === 'identity_theft' ||
                    payload.is_type === 'stolen_assets' ||
                    payload.is_type === 'no_payment' ||
                    payload.is_type === 'slow_payment' ||
                    payload.is_type === 'wrongful_rate_reduction'
                  "
                >
                  <label
                    class="text-xs font-bold uppercase text-dark-blue"
                    for="request"
                  >
                    LOAD ORIGIN
                    {{
                      payload.is_type === 'stolen_assets' ||
                      payload.is_type === 'double_brokering' ||
                      payload.is_type === 'no_payment' ||
                      payload.is_type === 'slow_payment' ||
                      payload.is_type === 'wrongful_rate_reduction'
                        ? '(REQUIRED)'
                        : ''
                    }}
                  </label>
                  <!-- Start ORIGIN Input  -->
                  <div class="-ml-1 flex flex-1 flex-col">
                    <div class="flex flex-row rounded-xl p-1">
                      <div class="relative flex w-full flex-row">
                        <div class="relative flex w-full flex-col">
                          <div
                            id="origin-wrap"
                            class="flex h-14 w-full cursor-pointer items-center rounded-md bg-off-white p-3 text-sm outline-none"
                            @click="
                              focusInput('origin'), activateCursor('origin')
                            "
                          >
                            <div
                              id="origin-text"
                              :class="{ hidden: origin?.showBadge }"
                              style="color: rgb(169, 173, 189)"
                            >
                              {{ originInputDisplayField }}
                              <span
                                id="origin-mock-cursor"
                                class="mock-cursor opacity-0"
                                :class="{
                                  hidden: !origin?.active || origin?.showBadge,
                                }"
                              />
                            </div>
                            <div
                              id="origin-badge"
                              class="flex items-center justify-center gap-2 rounded-full bg-dark-gray px-2 py-1 pl-4 text-xs text-white"
                              :class="{
                                hidden: !origin?.showBadge,
                              }"
                            >
                              <div class="whitespace-nowrap">
                                {{ origin.name }}
                              </div>
                              <div
                                class="relative z-10 inline-block h-6 w-6 cursor-pointer hover:opacity-80"
                                @click="unbadgify('origin')"
                              >
                                <svg
                                  class="relative h-4 w-4 cursor-pointer"
                                  style="top: 3.5px"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fill-rule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clip-rule="evenodd"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                          <input
                            id="origin-input"
                            v-model="originSearchText"
                            type="text"
                            class="-z-1 absolute inset-0 opacity-0"
                            @keydown.enter.prevent="onEnterOrigin"
                            @keydown.tab="tabInOrigin"
                            @keydown.tab.prevent="onEnterOrigin"
                            @keydown.up="focusOriginUp"
                            @keydown.down="focusOriginDown"
                            @blur="onBlurOrigin"
                            @input="
                              fetchAutocomplete(originSearchText, 'origin')
                            "
                          />
                        </div>
                        <div class="absolute inset-0 top-full">
                          <div>
                            <div class="relative -mt-1">
                              <ul
                                v-if="
                                  originSearchText?.length > 2 &&
                                  origin?.autocompleteResults?.length > 0
                                "
                                class="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                                tabindex="-1"
                                role="listbox"
                                aria-labelledby="listbox-label"
                                aria-activedescendant="listbox-option-3"
                              >
                                <!--
                                                         Select option, manage highlight styles based on mouseenter/mouseleave and keyboard navigation.

                                                         Highlighted: "text-white bg-yellow-600", Not Highlighted: "text-gray-900"
                                                       -->
                                <li
                                  v-for="(
                                    item, index
                                  ) in origin?.autocompleteResults"
                                  id="`origin-dropdown-value-${index}`"
                                  :key="`origin-dropdown-value-${index}`"
                                  class="group relative cursor-pointer select-none py-2 pl-8 pr-4 hover:bg-yellow-500 hover:text-white"
                                  role="option"
                                  :class="
                                    index === focusOrigin
                                      ? 'bg-yellow-500 text-white'
                                      : 'text-body-color'
                                  "
                                >
                                  <!-- Selected: "font-semibold", Not Selected: "font-medium" -->
                                  <span
                                    class="flex h-6 items-center truncate font-medium"
                                    @click="
                                      clickAutocompleteResult('origin', item)
                                    "
                                  >
                                    {{ item?.description }}
                                  </span>

                                  <!--
                                                           Checkmark, only display for selected option.

                                                           Highlighted: "text-white", Not Highlighted: "text-yellow-600"
                                                         -->
                                  <span
                                    v-if="originSearchText?.length > 0"
                                    class="absolute inset-y-0 left-0 flex items-center pl-1.5 group-hover:text-white"
                                    :class="
                                      index === focusOrigin
                                        ? 'text-white'
                                        : 'text-yellow-600'
                                    "
                                  >
                                    <svg
                                      class="h-5 w-5"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                      aria-hidden="true"
                                    >
                                      <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                      />
                                    </svg>
                                  </span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- End ORIGIN Input  -->
                </div>
                <div
                  v-if="payload.is_type !== 'change_in_ownership'"
                  class="relative w-full"
                >
                  <label
                    class="text-xs font-bold uppercase text-dark-blue"
                    for="request"
                  >
                    load commodity type
                    {{
                      payload.is_type === 'stolen_assets' ||
                      payload.is_type === 'hostage_load'
                        ? '(REQUIRED)'
                        : ''
                    }}
                  </label>
                  <dropdown
                    field="commodity"
                    :options="commodities"
                    name="types"
                    placeholder="Select commodity type"
                    @dropdown-input="updateCommodityType"
                  />
                </div>
                <div v-if="payload.is_type !== 'change_in_ownership'">
                  <label
                    class="text-xs font-bold uppercase"
                    :class="[
                      suspectEmailValidations.error
                        ? 'text-red-500'
                        : 'text-dark-blue',
                    ]"
                    for="request"
                  >
                    Suspect Email
                  </label>
                  <div class="relative mt-1">
                    <input
                      v-model="alertEmail"
                      class="ellipsis-overflow w-full rounded-lg p-4 text-sm"
                      :class="[
                        suspectEmailValidations.error
                          ? 'red-bg no-hover-outline bg-red-100'
                          : 'border-2 border-transparent bg-off-white focus:bg-white',
                      ]"
                      placeholder="Enter email address"
                      type="email"
                      style="padding-right: 1.5rem"
                    />
                    <span
                      v-if="suspectEmailValidations.error"
                      class="text-2xs text-red-500"
                      >{{ suspectEmailValidations.message }}</span
                    >
                  </div>
                </div>
                <div v-if="payload.is_type !== 'change_in_ownership'">
                  <label
                    class="text-xs font-bold uppercase text-dark-blue"
                    for="request"
                  >
                    Suspect Phone
                  </label>
                  <div class="mt-1">
                    <identity-alert-phone-input
                      v-model="alertPhoneNumber"
                      :auto-value="phoneAutoValue"
                      pattern="number"
                      @update-country-code="onUpdateCountryCode"
                    />
                  </div>
                </div>
              </div>
              <div class="mt-4">
                <label
                  class="text-xs font-bold text-dark-blue"
                  for="request"
                  >DETAILED INCIDENT SUMMARY
                </label>
                <div class="mt-2">
                  <textarea
                    v-model="alertText"
                    class="h-32 w-full rounded-lg border-2 border-transparent bg-off-white px-5 py-4 text-sm sm:text-base"
                    placeholder="Describe the incident with as much information as possible... (minimum 30 characters)"
                  />
                </div>
                <div class="mt-1 text-2xs leading-none text-body-color">
                  Explain the events that led to this alert. Use facts.
                </div>
              </div>
              <div class="mt-4 flex h-20 w-full items-center pb-6">
                <button-main
                  variant="secondary"
                  class="mr-2 h-full w-full"
                  @click="stepBack()"
                >
                  Back
                </button-main>
                <button-main
                  :disabled="!showSubmitFn"
                  class="h-full w-full"
                  @click="onSubmitCreateAlert()"
                >
                  Submit
                </button-main>
              </div>
            </div>
          </div>
          <div
            v-else-if="showCreateAlertSuccess"
            class="w-full"
            style="height: calc(100% - 88px)"
          >
            <div class="text-left text-sm text-body-color sm:text-base">
              We cannot personally acknowledge each submission, but we will
              reach out to you should our team have any questions. If we decide
              not to publish your alert, you will receive an automated email
              notification. Thanks for your submission.
            </div>
            <div class="mt-6 flex items-center justify-end pb-6">
              <button-main
                class="!w-max px-5 py-3"
                @click="onCancel"
              >
                Close
              </button-main>
            </div>
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { useStore } from 'vuex';
import { ref, toRef, computed, watch, onMounted, onUnmounted } from 'vue';
import dayjs from 'dayjs';
import ButtonMain from '@/components/Button.vue';
import Modal from '@/components/modals/Modal.vue';
import InformationPanel from '@/components/InformationPanel.vue';
import Checkbox from '@/components/Checkbox.vue';
import FormFieldDate from '@/components/FormFieldDate.vue';
import DatePicker from '@/components/DatePicker.vue';
import IdentityAlertPhoneInput from '@/components/IdentityAlertPhoneInput.vue';
import Dropdown from '@/components/Dropdown.vue';

export default {
  name: 'IdentityAlertModal',
  components: {
    Modal,
    InformationPanel,
    Checkbox,
    FormFieldDate,
    DatePicker,
    ButtonMain,
    IdentityAlertPhoneInput,
    Dropdown,
  },
  props: {
    visible: Boolean,
    showCreateAlertSuccess: Boolean,
    showEmailAndPhone: {
      type: Boolean,
      default: false,
    },
    incidentEmail: {
      type: String,
      required: false,
    },
    incidentPhone: {
      type: [String, Number],
      required: false,
    },
  },
  emits: ['close', 'submitAlert'],
  setup(props, { emit }) {
    const store = useStore();
    const incidentEmailVal = toRef(props, 'incidentEmail');
    const incidentPhoneVal = toRef(props, 'incidentPhone');
    const alertText = ref('');
    const alertPhoneNumber = ref(null);
    const alertEmail = ref(null);
    const allowContinue = ref(false);
    const allowContinue2 = ref(false);
    const showSubmit = ref(false);
    const showDatePicker = ref(false);
    const step = ref(1);
    const initialDate = computed(() => {
      return dayjs().format('YYYY-MM-DD');
    });
    const selectedAlertDate = ref('');
    const phoneAutoValue = ref('');
    const focusOrigin = ref(undefined);
    const originPlaceholder = ref('Enter city or postal code ');
    selectedAlertDate.value = dayjs().format('MM/DD/YYYY');
    const payload = ref({
      is_type: null,
      message: null,
      file_date: null,
      email: null,
      phone_e164: null,
      country_code: null,
      origin_postal_code: null,
      origin_area_id: null,
      commodity_type_id: null,
    });

    const origin = computed({
      get() {
        return store.state.laneSearches.origin;
      },
      set(newOrigin) {
        this.$store.commit('laneSearches/updateOrigin', newOrigin);
      },
    });

    const originInputDisplayField = computed(() => {
      if (origin?.value.name?.length > 0) {
        return origin.value.name;
      } else if (originSearchText?.value.length > 0) {
        return originSearchText.value;
      } else {
        return originPlaceholder.value;
      }
    });

    const commodities = computed(() => {
      const fields = store.state.identityAlerts.commodities;

      if (fields?.length > 0) {
        return fields.map((field) => {
          return {
            label: field.name,
            parragraph: '',
            id: field.id,
          };
        });
      }

      return [];
    });

    const closeModal = async () => {
      emit('close');
      await store.commit('laneSearches/resetFieldAlerts', 'origin');
    };

    const showSubmitFn = computed(() => {
      const updatedPayload = payload.value;

      if (suspectEmailValidations.value.error) return false;

      const isValidEmail = (email) => {
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
        return emailRegex.test(email);
      };

      const isValidPhoneE164 = (phone) => {
        const re = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;
        return re.test(phone);
      };

      const isValidField = (field, value) => {
        if (value === null || value === undefined) return false;

        if (typeof value === 'string') {
          if (field === 'message') {
            return value.trim().length >= 30;
          } else if (field === 'email') {
            return isValidEmail(value.trim());
          } else if (field === 'phone_e164') {
            return isValidPhoneE164(value.trim());
          } else {
            return value.trim().length > 0;
          }
        }

        return true;
      };

      const requiredFields = ['is_type', 'message', 'file_date'];
      if (
        [
          'double_brokering',
          'stolen_assets',
          'no_payment',
          'slow_payment',
          'wrongful_rate_reduction',
        ].includes(updatedPayload.is_type)
      ) {
        requiredFields.push('origin_postal_code', 'origin_area_id');
      }

      const isRequiredFieldsFilled = requiredFields.every((field) =>
        isValidField(field, updatedPayload[field])
      );
      if (!isRequiredFieldsFilled) return false;

      if (
        updatedPayload.is_type === 'identity_theft' &&
        !isValidField('email', updatedPayload.email) &&
        !isValidField('phone_e164', updatedPayload.phone_e164)
      ) {
        return false;
      }

      if (updatedPayload.is_type === 'stolen_assets') {
        return isValidField(
          'commodity_type_id',
          updatedPayload['commodity_type_id']
        );
      }

      return true;
    });

    const isValidEmail = (email) => {
      const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
      return regex?.test(email);
    };

    const extractBaseDomain = (email) => {
      if (!email) return;

      const domain = email?.split('@')?.[1];
      return domain?.split('.')[0];
    };

    const emailAndDomainBrokerUser = computed(() => {
      const user = store.state.users.currentUser;
      const email = user?.email;
      const domain = extractBaseDomain(email);
      return { email, domain };
    });

    const suspectEmailValidations = computed(() => {
      const email = alertEmail.value;
      if (!isValidEmail(email)) return { error: false, message: '' };

      const domain = extractBaseDomain(email);
      const { email: emailCurrentUser, domain: domainBrokerUser } =
        emailAndDomainBrokerUser.value;

      const commonEmailDomains = [
        'gmail.com',
        'yahoo.com',
        'hotmail.com',
        'aol.com',
        'msn.com',
        'outlook.com',
        'live.com',
        'icloud.com',
        'mail.com',
        'zoho.com',
      ];

      const emailDomainFull = email.split('@')[1];

      if (commonEmailDomains.includes(emailDomainFull)) {
        if (email === emailCurrentUser) {
          return {
            error: true,
            message:
              'Please enter the email of the bad actor you are reporting.',
          };
        }
      } else if (domain === domainBrokerUser) {
        return {
          error: true,
          message: 'Please enter the email of the bad actor you are reporting.',
        };
      }

      return { error: false };
    });

    const updateCommodityType = (commodityType) => {
      payload.value.commodity_type_id = commodityType?.id;
    };

    const clickAutocompleteResult = async (isType, googlePlace) => {
      await store.dispatch('laneSearches/updateFromGooglePlace', {
        isType: isType,
        googlePlace: googlePlace,
      });
      switch (isType) {
        case 'origin':
          store.commit('laneSearches/updateOrigin', {
            searchText: '',
            autocompleteResults: [],
            showBadge: true,
          });
          activeInputToggle(isType);
          break;
        case 'destination':
          store.commit('laneSearches/updateDestination', {
            searchText: '',
            autocompleteResults: [],
            showBadge: true,
          });
          break;
      }
      const areaInformation = await store.dispatch(
        'laneSearches/updateAreaIdFromGooglePlace',
        isType
      );
      if (areaInformation?.data) {
        payload.value.origin_area_id = `${areaInformation?.data?.id}`;
        payload.value.origin_postal_code = areaInformation?.data?.code;
      }
    };

    const activeInputToggle = (value) => {
      store.commit('laneSearches/updateOrigin', {
        active: value == 'origin',
      });
    };

    const originSearchText = computed({
      get() {
        return store.state.laneSearches.origin.searchText;
      },
      set(value) {
        store.commit('laneSearches/updateOrigin', { searchText: value });
      },
    });
    payload.value.file_date = initialDate.value;

    const createAlertConfirmation = (val) => {
      if (val == true || val == false) {
        allowContinue.value = val;
      }
    };

    const stepForward = () => {
      step.value = step.value + 1;
    };

    const stepBack = () => {
      if (step.value - 1 == 1) {
        allowContinue.value = false;
      }
      if (step.value - 1 == 2) {
        allowContinue2.value = false;
      }
      step.value = step.value - 1;
    };

    const updateIsType = (option) => {
      console.log('updateIsType option...', option);
      if (option == null) {
        payload.value.is_type = null;
      } else {
        payload.value.is_type = option?.id;
      }
      if (payload.value.is_type !== null) {
        allowContinue2.value = true;
      } else {
        allowContinue2.value = false;
      }
    };

    function toggleShowDatePicker() {
      showDatePicker.value = !showDatePicker.value;
    }

    function setSelectedDay(fieldValue, dateSelected) {
      if (fieldValue) {
        selectedAlertDate.value = dateSelected;
        dynamicUpdateDate({ name: fieldValue, value: dateSelected });
        showDatePicker.value = false;
        // if (overrideOption.value == 'choose_expiration_date') {
        //     buttonDisabled.value = false;
        // }
      }
    }

    function dynamicUpdateDate({ value }) {
      let submitVal = dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD');
      payload.value.file_date = submitVal;
    }

    onMounted(() => {
      if (incidentEmailVal?.value !== null) {
        alertEmail.value = incidentEmailVal.value;
      }
      if (incidentPhoneVal?.value !== null) {
        phoneAutoValue.value = incidentPhoneVal.value;
        alertPhoneNumber.value = incidentPhoneVal.value;
      }

      if (commodities.value.length > 0) return;
      store.dispatch('identityAlerts/getCommodities');
    });

    onUnmounted(() => {
      unbadgify('origin');
    });

    watch(alertText, (newVal) => {
      if (newVal !== null) {
        payload.value.message = newVal;
      }
      // console.log('payload...');
      // console.dir(payload);
      // console.log(payload.value);
      // console.log('alertText watch running...')
      // console.log(payload.value.carrier_id);
      // console.log(payload.value['carrier_id']);
      // if (payload.value['carrier_id'] > 0 && payload.value['is_type'] !== null && payload.value['file_date'] !== null && payload.value['message']?.length > 29) {
      //     showSubmit.value = true;
      // } else if ((payload.value['email']?.length > 0 || payload.value['phone_e164']?.length > 0) && payload.value['is_type'] !== null && payload.value['file_date'] !== null && payload.value['message']?.length > 29) {
      //     showSubmit.value = true;
      // } else {
      //     showSubmit.value = false;
      // }
      // console.log('alertText watch done.')
    });

    watch(alertEmail, (newVal) => {
      if (newVal !== null) {
        payload.value.email = newVal;
      }
    });

    watch(alertPhoneNumber, (newVal) => {
      if (newVal !== null) {
        payload.value.phone_e164 = newVal;
      }
    });

    const onSubmitCreateAlert = () => {
      emit('submitAlert', payload.value);
    };

    const onCancel = () => {
      payload.value = {
        is_type: null,
        message: null,
        file_date: null,
        email: null,
        phone_e164: null,
        phone: null,
        country_code: null,
      };
      emit('close');
    };

    const onUpdateCountryCode = (val) => {
      payload.value.country_code = val;
    };

    const focusInput = (inputId) => {
      var inputEl;
      switch (inputId) {
        case 'origin':
          inputEl = document.getElementById('origin-input');
          inputEl.focus();
          break;
        case 'destination':
          inputEl = document.getElementById('destination-input');
          inputEl.focus();
          break;
      }
    };

    const activateCursor = () => {
      let cursor;
      originPlaceholder.value = '';
      cursor = document.getElementById('origin-mock-cursor');
      cursor.classList.remove('opacity-0');
      cursor.classList.add('blink');
    };

    const focusOriginUp = () => {
      if (focusOrigin.value === 0) {
        return;
      }
      focusOrigin.value =
        focusOrigin.value !== undefined ? focusOrigin.value - 1 : 0;
    };

    const fetchAutocomplete = async (term, isType) => {
      // var self = this;
      if (term.length > 2) {
        await store.dispatch('laneSearches/getAutocomplete', {
          isType: isType,
          term: term,
        });
        // if (res.status == 200) {
        //   // switch(isType) {
        //   //   case 'origin':
        //   //     this.origin?.autocompleteResults = res.data;
        //   //     break;
        //   //   case 'destination':
        //   //     this.destination?.autocompleteResults = res.data;
        //   //     break;
        //   // }
        // } else {
        //   //handle error codes 401, 403, 422
        //   toastResponse(this, res, null);
        //   throw Error(res);
        // }
      } else {
        // this.origin?.autocompleteResults = [];
      }
    };
    const focusOriginDown = () => {
      if (!origin.value.autocompleteResults) {
        focusOrigin.value = undefined;
        return;
      }

      if (focusOrigin.value === origin?.value.autocompleteResults?.length - 1) {
        return;
      }
      focusOrigin.value =
        focusOrigin.value !== undefined ? focusOrigin.value + 1 : 0;
    };
    const onBlurOrigin = () => {
      focusOrigin.value = undefined;
    };
    const onEnterOrigin = () => {
      if (focusOrigin.value === undefined) {
        return;
      }
      const item = origin.value.autocompleteResults[focusOrigin.value];
      clickAutocompleteResult('origin', item);
    };

    const unbadgify = async (isType) => {
      await store.commit('laneSearches/resetFieldAlerts', isType);

      payload.value.origin_area_id = null;
      payload.value.origin_postal_code = null;
    };

    return {
      onCancel,
      onSubmitCreateAlert,
      createAlertConfirmation,
      allowContinue,
      allowContinue2,
      step,
      stepForward,
      stepBack,
      updateIsType,
      payload,
      setSelectedDay,
      initialDate,
      selectedAlertDate,
      dynamicUpdateDate,
      showDatePicker,
      toggleShowDatePicker,
      alertText,
      showSubmit,
      alertPhoneNumber,
      alertEmail,
      phoneAutoValue,
      onUpdateCountryCode,
      onEnterOrigin,
      onBlurOrigin,
      focusOriginDown,
      focusOriginUp,
      focusOrigin,
      closeModal,
      activateCursor,
      focusInput,
      fetchAutocomplete,
      origin,
      originInputDisplayField,
      originSearchText,
      activeInputToggle,
      clickAutocompleteResult,
      unbadgify,
      showSubmitFn,
      updateCommodityType,
      commodities,
      suspectEmailValidations,
    };
  },
  computed: {
    informationPanels() {
      return this.$store.state.identityAlerts.informationPanels;
    },
    alertCategories() {
      return this.$store.state.identityAlerts.alertCategories;
    },
    checkboxText() {
      return 'I understand these guidelines';
    },
  },
};
</script>
<style scoped>
textarea {
  border: 1px solid rgb(247, 247, 250);
}

textarea:hover {
  border: 1px solid #6d7181;
}

input {
  border-radius: 5px;
}
.red-bg {
  border: 1px solid rgb(219 55 19);
}

.no-hover-outline:hover {
  outline: none;
}

.no-hover-outline:focus {
  outline: none;
}
</style>
